import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    Upload,
    ArrowLeft,
    Check,
    AlertCircle,
    Smartphone,
    Package,
    Settings,
    FileSpreadsheet,
    Play,
    Loader2,
    ChevronDown,
    X,
    Info,
    Eye,
    FileText
} from 'lucide-react';
import * as Papa from 'papaparse';
import * as XLSX from 'xlsx';
import api from '../services/api';
import { useWorkspace } from '../context/WorkspaceContext';
import DevicePicker from '../components/DevicePicker';
import PremiumBadge from '../components/PremiumBadge';
import './ImportGoals.css';

function ImportGoals() {
    const navigate = useNavigate();
    const { currentWorkspace } = useWorkspace();
    const realDevicesEnabled = currentWorkspace?.subscription?.quotas?.realDevicesEnabled === true;

    // Workspace Apps
    const [apps, setApps] = useState([]);
    const [appsLoading, setAppsLoading] = useState(false);
    const [selectedApp, setSelectedApp] = useState(null);
    const [showAppPicker, setShowAppPicker] = useState(false);

    // File Upload & Raw Data Grid (array of rows, where each row is array of cells)
    const [fileName, setFileName] = useState('');
    const [rawGrid, setRawGrid] = useState([]); // [[cell, cell, ...], ...]
    const [headers, setHeaders] = useState([]); // List of column names in selected header row
    const [headerRowIndex, setHeaderRowIndex] = useState(0); // 0-indexed row number of header

    // Mappings: Target Field -> Column Index (or name)
    const [mappings, setMappings] = useState({
        goalTextColumns: [],
        title: '',
        priority: '',
        category: '',
        tags: ''
    });

    // Row Detail Modal for preview inspection
    const [selectedRowDetail, setSelectedRowDetail] = useState(null);

    // Parsed Goals preview & Validation
    const [previewGoals, setPreviewGoals] = useState([]);
    const [isValidating, setIsValidating] = useState(false);
    const [importError, setImportError] = useState('');
    const [importSuccess, setImportSuccess] = useState('');

    // Execution immediately options
    const [executeImmediately, setExecuteImmediately] = useState(false);
    const [executionMode, setExecutionMode] = useState('individual'); // 'individual' or 'execution_plan'
    const [selectedDevice, setSelectedDevice] = useState({
        arn: 'auto',
        name: 'Auto (pick best device)',
        model: 'Automatic Selection',
        os: 'Best Available',
        platform: 'android',
        availability: 'AVAILABLE',
        provider: 'aws'
    });
    const [showDevicePicker, setShowDevicePicker] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Load Apps for current workspace
    const loadApps = useCallback(async () => {
        if (!currentWorkspace?.id) return;
        setAppsLoading(true);
        try {
            const response = await api.get('/apps', {
                params: { workspaceId: currentWorkspace.id, limit: 50 }
            });
            const rows = response.data.rows || [];
            setApps(rows);
            if (rows.length > 0) {
                // Pre-select first app
                setSelectedApp(rows[0]);
            }
        } catch (err) {
            console.error('Failed to load apps:', err);
        } finally {
            setAppsLoading(false);
        }
    }, [currentWorkspace?.id]);

    useEffect(() => {
        loadApps();
    }, [loadApps]);

    // Handle App Platform and Device reset
    useEffect(() => {
        if (selectedApp) {
            const appPlatform = selectedApp.platform.toLowerCase();
            if (appPlatform === 'android' && !realDevicesEnabled) {
                setSelectedDevice({
                    arn: 'emulator',
                    name: 'Android Emulator',
                    model: 'Standard Emulator',
                    os: 'API 30+',
                    platform: 'android',
                    availability: 'AVAILABLE',
                    provider: 'emulator'
                });
            } else {
                setSelectedDevice({
                    arn: 'auto',
                    name: 'Auto (pick best device)',
                    model: 'Automatic Selection',
                    os: 'Best Available',
                    platform: appPlatform,
                    availability: 'AVAILABLE',
                    provider: 'aws'
                });
            }
        }
    }, [selectedApp, realDevicesEnabled]);

    // File Parsing Trigger
    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setFileName(file.name);
        setImportError('');
        setImportSuccess('');

        const ext = file.name.split('.').pop().toLowerCase();
        if (ext === 'csv') {
            const reader = new FileReader();
            reader.onload = (event) => {
                const text = event.target.result;
                Papa.parse(text, {
                    skipEmptyLines: 'greedy',
                    complete: (results) => {
                        processGrid(results.data);
                    },
                    error: (error) => {
                        setImportError(`CSV parsing error: ${error.message}`);
                    }
                });
            };
            reader.readAsText(file);
        } else if (ext === 'xlsx' || ext === 'xls') {
            const reader = new FileReader();
            reader.onload = (event) => {
                const data = new Uint8Array(event.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const dataGrid = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
                processGrid(dataGrid);
            };
            reader.readAsArrayBuffer(file);
        } else {
            setImportError('Unsupported file type. Please upload a .csv, .xlsx, or .xls file.');
        }
    };

    // Process raw grid data and auto-detect Header row
    const processGrid = (grid) => {
        if (!grid || grid.length === 0) {
            setImportError('File is empty.');
            return;
        }

        setRawGrid(grid);

        // Auto-detect header row containing keywords like goal, description, test, priority, severity
        let autoHeaderIdx = 0;
        const keywords = ['goal', 'description', 'test', 'priority', 'severity', 'tags', 'label'];

        for (let r = 0; r < Math.min(grid.length, 50); r++) {
            const row = grid[r];
            if (Array.isArray(row)) {
                const containsKeyword = row.some(cell => {
                    const cellStr = String(cell).toLowerCase();
                    return keywords.some(kw => cellStr.includes(kw));
                });
                if (containsKeyword) {
                    autoHeaderIdx = r;
                    break;
                }
            }
        }

        setHeaderRowIndex(autoHeaderIdx);
        updateHeadersAndAutoMap(grid, autoHeaderIdx);
    };

    const updateHeadersAndAutoMap = (grid, headerIdx) => {
        const headerRow = grid[headerIdx] || [];
        // Map elements to strings for headers. Fill in empty elements
        const cleanedHeaders = headerRow.map((h, i) => String(h).trim() || `Column ${i + 1}`);
        setHeaders(cleanedHeaders);

        // Auto-mapping logic
        const newMappings = { goalTextColumns: [], title: '', priority: '', category: '', tags: '' };
        cleanedHeaders.forEach((name, idx) => {
            const nameLower = name.toLowerCase();
            if (
                nameLower.includes('title') ||
                nameLower.includes('case name') ||
                nameLower.includes('test name') ||
                nameLower.includes('subject')
            ) {
                if (!newMappings.title) newMappings.title = String(idx);
            } else if (
                nameLower.includes('goal') ||
                nameLower.includes('desc') ||
                nameLower.includes('objective') ||
                nameLower.includes('precondition') ||
                nameLower.includes('step') ||
                nameLower.includes('postcondition') ||
                nameLower.includes('expected')
            ) {
                newMappings.goalTextColumns.push(String(idx));
            } else if (nameLower.includes('priority') || nameLower.includes('sever')) {
                if (!newMappings.priority) newMappings.priority = String(idx);
            } else if (
                nameLower.includes('category') ||
                nameLower.includes('cat') ||
                nameLower.includes('folder') ||
                nameLower.includes('group') ||
                nameLower.includes('suite') ||
                nameLower.includes('module') ||
                nameLower.includes('feature')
            ) {
                if (!newMappings.category) newMappings.category = String(idx);
            } else if (nameLower.includes('tag') || nameLower.includes('label')) {
                if (!newMappings.tags) newMappings.tags = String(idx);
            }
        });

        // Fallbacks if no direct match
        if (newMappings.goalTextColumns.length === 0 && cleanedHeaders.length > 0) {
            newMappings.goalTextColumns = ['0']; // default first column
        }

        setMappings(newMappings);
    };

    // Apply header index changes
    const handleHeaderRowIndexChange = (idx) => {
        setHeaderRowIndex(idx);
        updateHeadersAndAutoMap(rawGrid, idx);
    };

    // Parse and validate rows on change of mapping or header row index
    useEffect(() => {
        if (rawGrid.length === 0 || headers.length === 0) return;

        setIsValidating(true);
        const dataRows = rawGrid.slice(headerRowIndex + 1);

        const goals = dataRows.map((row, rIdx) => {
            const originalRowIdx = headerRowIndex + 1 + rIdx + 1; // 1-indexed Excel row

            // Extract values
            let goalTextVal = '';
            if (mappings.goalTextColumns && mappings.goalTextColumns.length > 0) {
                const parts = mappings.goalTextColumns.map(colIdx => {
                    const headerName = headers[parseInt(colIdx)];
                    const cellVal = String(row[parseInt(colIdx)] || '').trim();
                    return cellVal ? `${headerName}: ${cellVal}` : '';
                }).filter(Boolean);
                goalTextVal = parts.join('\n');
            }

            const titleVal = mappings.title !== '' ? String(row[parseInt(mappings.title)] || '').trim() : '';
            const priorityVal = mappings.priority !== '' ? String(row[parseInt(mappings.priority)] || '').trim() : 'medium';
            const categoryVal = mappings.category !== '' ? String(row[parseInt(mappings.category)] || '').trim() : '';
            const tagsVal = mappings.tags !== '' ? String(row[parseInt(mappings.tags)] || '').trim() : '';

            // Validation checks
            const errors = [];
            if (!goalTextVal) {
                errors.push('Goal Text is required');
            }

            let resolvedPriority = 'medium';
            if (priorityVal) {
                const pLower = priorityVal.toLowerCase().trim();
                if (['low', 'medium', 'high', 'critical'].includes(pLower)) {
                    resolvedPriority = pLower;
                } else if (mappings.priority !== '') {
                    errors.push(`Invalid Priority: "${priorityVal}" (supported: low, medium, high, critical)`);
                }
            }

            const tagsArray = tagsVal
                ? tagsVal.split(',').map(t => t.trim()).filter(t => t !== '')
                : [];

            const categoriesArray = categoryVal
                ? categoryVal.split(',').map(c => c.trim()).filter(c => c !== '')
                : [];

            return {
                rowNumber: originalRowIdx,
                title: titleVal,
                goalText: goalTextVal,
                priority: resolvedPriority,
                categories: categoriesArray,
                tags: tagsArray,
                rawData: row,
                errors,
                isValid: errors.length === 0
            };
        }).filter(g => g.goalText || g.errors.length > 0); // Filter completely empty data rows

        setPreviewGoals(goals);
        setIsValidating(false);

    }, [rawGrid, headerRowIndex, mappings, headers]);

    // Handle mapping selection changes
    const handleMappingChange = (field, colIndex) => {
        setMappings(prev => ({
            ...prev,
            [field]: colIndex
        }));
    };

    // Submit import to Backend
    const handleImportSubmit = async () => {
        if (!selectedApp) {
            setImportError('Please select an App to run/import these test goals against.');
            return;
        }

        const validGoalsToSubmit = previewGoals.filter(g => g.isValid);
        if (validGoalsToSubmit.length === 0) {
            setImportError('No valid test goals to import.');
            return;
        }

        setIsSubmitting(true);
        setImportError('');
        setImportSuccess('');

        try {
            const payload = {
                appId: selectedApp.id,
                executeImmediately,
                executionMode,
                deviceArn: executeImmediately ? selectedDevice.arn : null,
                deviceProvider: executeImmediately ? selectedDevice.provider : null,
                goals: validGoalsToSubmit.map(g => ({
                    title: g.title || null,
                    goalText: g.goalText,
                    priority: g.priority,
                    categories: g.categories,
                    tags: g.tags,
                    rowNumber: g.rowNumber
                }))
            };

            const response = await api.post('/tests/goals/bulk-import', payload);
            setImportSuccess(`Successfully imported ${response.data.importedCount} test goals.`);

            // Redirect to Goals List or Execution Plan after brief delay
            setTimeout(() => {
                if (executeImmediately) {
                    if (response.data.executionPlanExecutionId) {
                        navigate(`/execution-plans/executions/${response.data.executionPlanExecutionId}`);
                    } else {
                        navigate('/executions');
                    }
                } else {
                    navigate('/goals');
                }
            }, 2000);

        } catch (error) {
            console.error('Import failed:', error);
            setImportError(error.response?.data?.error || 'Failed to complete import.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const validCount = previewGoals.filter(g => g.isValid).length;
    const invalidCount = previewGoals.length - validCount;

    return (
        <div className="import-goals-container">
            <div className="import-goals-header">
                <Link to="/goals" className="back-btn">
                    <ArrowLeft size={16} />
                    Back to Goals
                </Link>
                <h1>Import Test Goals</h1>
                <p className="subtitle">Upload Excel or CSV sheets to batch import structured mobile test goals</p>
            </div>

            <div className="import-layout">
                {/* Left Panel: Configuration & File Upload */}
                <div className="import-config-card">
                    <div className="config-section">
                        <h2>1. Target Mobile App</h2>
                        <div className="app-picker-container">
                            <button
                                className="app-picker-btn"
                                onClick={() => setShowAppPicker(!showAppPicker)}
                            >
                                <Package size={16} />
                                {selectedApp ? (
                                    <span className="app-name-display">{selectedApp.displayName || selectedApp.name} ({selectedApp.platform})</span>
                                ) : (
                                    <span>Select App</span>
                                )}
                                <ChevronDown size={16} className="chevron" />
                            </button>

                            {showAppPicker && (
                                <div className="app-picker-dropdown">
                                    {appsLoading ? (
                                        <div className="dropdown-loading"><Loader2 size={16} className="animate-spin" /> Loading apps...</div>
                                    ) : apps.length === 0 ? (
                                        <div className="dropdown-empty">No apps in workspace.</div>
                                    ) : (
                                        apps.map(app => (
                                            <div
                                                key={app.id}
                                                className={`dropdown-item ${selectedApp?.id === app.id ? 'active' : ''}`}
                                                onClick={() => {
                                                    setSelectedApp(app);
                                                    setShowAppPicker(false);
                                                }}
                                            >
                                                <strong>{app.displayName || app.name}</strong>
                                                <span className="app-meta">{app.platform} • v{app.version || '1.0'}</span>
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="config-section">
                        <h2>2. Upload Excel / CSV file</h2>
                        <label className="file-uploader-box">
                            <Upload size={32} />
                            <span>{fileName || "Drag & drop or browse"}</span>
                            <span className="file-formats">Supports .csv, .xlsx, .xls</span>
                            <input
                                type="file"
                                accept=".csv,.xlsx,.xls"
                                onChange={handleFileUpload}
                                style={{ display: 'none' }}
                            />
                        </label>
                    </div>

                    {rawGrid.length > 0 && (
                        <>
                            <div className="config-section">
                                <h2>3. Preamble & Header Configuration</h2>
                                <div className="header-row-picker">
                                    <label htmlFor="header-row-select">Header Row Index:</label>
                                    <div className="header-row-select-container">
                                        <select
                                            id="header-row-select"
                                            value={headerRowIndex}
                                            onChange={(e) => handleHeaderRowIndexChange(parseInt(e.target.value))}
                                        >
                                            {rawGrid.slice(0, Math.min(rawGrid.length, 50)).map((row, idx) => (
                                                <option key={idx} value={idx}>
                                                    Row {idx + 1}: {row.slice(0, 3).join(', ')}...
                                                </option>
                                            ))}
                                        </select>
                                        <input
                                            type="number"
                                            min="1"
                                            max={rawGrid.length}
                                            value={headerRowIndex + 1}
                                            onChange={(e) => {
                                                const val = parseInt(e.target.value);
                                                if (val >= 1 && val <= rawGrid.length) {
                                                    handleHeaderRowIndexChange(val - 1);
                                                }
                                            }}
                                        />
                                    </div>
                                    <p className="input-tip">If your file has introductory text/preamble, select or type the row where headers start.</p>
                                </div>
                            </div>

                            <div className="config-section">
                                <h2>4. Field Column Mapping</h2>
                                <div className="mappings-form">
                                    <div className="mapping-field">
                                        <label>Goal Text Columns <span className="required">*</span></label>
                                        <div className="field-note">
                                            <Info size={14} className="note-icon" />
                                            <span>
                                                Select one or multiple columns (e.g. <em>Preconditions</em>, <em>Steps</em>, <em>Expected Result</em>). All selected columns will be concatenated into a single structured description for AI execution.
                                            </span>
                                        </div>
                                        <div className="columns-checkbox-list">
                                            {headers.map((h, i) => (
                                                <label key={i} className="column-checkbox-item">
                                                    <input
                                                        type="checkbox"
                                                        checked={mappings.goalTextColumns.includes(String(i))}
                                                        onChange={(e) => {
                                                            const checked = e.target.checked;
                                                            setMappings(prev => {
                                                                const cols = [...prev.goalTextColumns];
                                                                if (checked) {
                                                                    cols.push(String(i));
                                                                } else {
                                                                    const idx = cols.indexOf(String(i));
                                                                    if (idx > -1) cols.splice(idx, 1);
                                                                }
                                                                return { ...prev, goalTextColumns: cols };
                                                            });
                                                        }}
                                                    />
                                                    <span>{h}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="mapping-field">
                                        <label>Title</label>
                                        <select
                                            value={mappings.title}
                                            onChange={(e) => handleMappingChange('title', e.target.value)}
                                        >
                                            <option value="">None (Optional short title)</option>
                                            {headers.map((h, i) => (
                                                <option key={i} value={i}>{h}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="mapping-field">
                                        <label>Priority</label>
                                        <select
                                            value={mappings.priority}
                                            onChange={(e) => handleMappingChange('priority', e.target.value)}
                                        >
                                            <option value="">None (Default to Medium)</option>
                                            {headers.map((h, i) => (
                                                <option key={i} value={i}>{h}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="mapping-field">
                                        <label>Category</label>
                                        <select
                                            value={mappings.category}
                                            onChange={(e) => handleMappingChange('category', e.target.value)}
                                        >
                                            <option value="">None</option>
                                            {headers.map((h, i) => (
                                                <option key={i} value={i}>{h}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="mapping-field">
                                        <label>Tags</label>
                                        <select
                                            value={mappings.tags}
                                            onChange={(e) => handleMappingChange('tags', e.target.value)}
                                        >
                                            <option value="">None</option>
                                            {headers.map((h, i) => (
                                                <option key={i} value={i}>{h}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* <div className="config-section execution-config-box">
                                <h2>5. Post-Import Execution Options</h2>
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={executeImmediately}
                                        onChange={(e) => setExecuteImmediately(e.target.checked)}
                                    />
                                    <span>Execute all imported goals immediately</span>
                                </label>

                                {executeImmediately && (
                                    <div className="post-import-mode-wrapper">
                                        <div className="mode-selection-header">
                                            <span className="mode-label">Execution Mode:</span>
                                        </div>
                                        <div className="mode-options-grid">
                                            <label
                                                className={`mode-option-card ${executionMode === 'individual' ? 'selected' : ''}`}
                                                onClick={() => setExecutionMode('individual')}
                                            >
                                                <input
                                                    type="radio"
                                                    name="importExecutionMode"
                                                    value="individual"
                                                    checked={executionMode === 'individual'}
                                                    onChange={() => setExecutionMode('individual')}
                                                />
                                                <div className="mode-card-text">
                                                    <strong>⚡ Individual Executions</strong>
                                                    <span>Queue goals independently on separate task slots</span>
                                                </div>
                                            </label>

                                            <label
                                                className={`mode-option-card ${executionMode === 'execution_plan' ? 'selected' : ''}`}
                                                onClick={() => setExecutionMode('execution_plan')}
                                            >
                                                <input
                                                    type="radio"
                                                    name="importExecutionMode"
                                                    value="execution_plan"
                                                    checked={executionMode === 'execution_plan'}
                                                    onChange={() => setExecutionMode('execution_plan')}
                                                />
                                                <div className="mode-card-text">
                                                    <strong>📋 Execution Plan Run</strong>
                                                    <span>Run sequentially in a single unified session with context reuse</span>
                                                </div>
                                            </label>
                                        </div>
                                    </div>
                                )}
                            </div> */}
                        </>
                    )}
                </div>

                {/* Right Panel: Live Parsing Preview & Action */}
                <div className="import-preview-card">
                    {previewGoals.length === 0 ? (
                        <div className="empty-preview-state">
                            <FileSpreadsheet size={48} />
                            <h3>No file uploaded</h3>
                            <p>Upload a CSV or Excel sheet and configure the mapping to view goal validation previews.</p>
                        </div>
                    ) : (
                        <div className="preview-container">
                            <div className="preview-stats-bar">
                                <span className="preview-title">Import Preview ({previewGoals.length} rows detected)</span>
                                <div className="stats-badges">
                                    <span className="badge badge-success">
                                        <Check size={12} />
                                        {validCount} Valid
                                    </span>
                                    {invalidCount > 0 && (
                                        <span className="badge badge-error">
                                            <AlertCircle size={12} />
                                            {invalidCount} Invalid (Will be skipped)
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="preview-table-container">
                                <p className="preview-click-hint">
                                    <Eye size={13} /> Click any row to inspect full mapped details &amp; raw row data
                                </p>
                                <table className="preview-table">
                                    <thead>
                                        <tr>
                                            <th>Row</th>
                                            <th>Title</th>
                                            <th>Goal Description</th>
                                            <th>Priority</th>
                                            <th>Category</th>
                                            <th>Tags</th>
                                            <th>Validation</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {previewGoals.map((g, index) => (
                                            <tr
                                                key={index}
                                                className={`preview-row ${g.isValid ? 'row-valid' : 'row-invalid'}`}
                                                onClick={() => setSelectedRowDetail({ goal: g, rowIndex: index })}
                                                title="Click to view full mapped details"
                                            >
                                                <td className="cell-row-num">{g.rowNumber}</td>
                                                <td className="cell-title">{g.title || <em className="empty-val">-</em>}</td>
                                                <td className="cell-goal-text">{g.goalText || <em className="empty-val">(Empty Goal)</em>}</td>
                                                <td className="cell-priority"><span className={`priority-pill priority-${g.priority}`}>{g.priority}</span></td>
                                                <td className="cell-category">
                                                    {g.categories && g.categories.length > 0 ? (
                                                        g.categories.map((c, ci) => <span key={ci} className="category-pill">{c}</span>)
                                                    ) : (
                                                        <span className="empty-val">-</span>
                                                    )}
                                                </td>
                                                <td className="cell-tags">
                                                    {g.tags && g.tags.length > 0 ? (
                                                        g.tags.map((t, ti) => <span key={ti} className="tag-pill">{t}</span>)
                                                    ) : (
                                                        <span className="empty-val">-</span>
                                                    )}
                                                </td>
                                                <td className="cell-status">
                                                    {g.isValid ? (
                                                        <span className="status-indicator status-valid">
                                                            <Check size={12} /> Valid
                                                        </span>
                                                    ) : (
                                                        <span className="status-indicator status-invalid" title={g.errors.join(', ')}>
                                                            <AlertCircle size={12} /> Error
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {importError && (
                                <div className="feedback-alert error-alert">
                                    <AlertCircle size={16} />
                                    <span>{importError}</span>
                                </div>
                            )}

                            {importSuccess && (
                                <div className="feedback-alert success-alert">
                                    <Check size={16} />
                                    <span>{importSuccess}</span>
                                </div>
                            )}

                            <div className="preview-action-footer">
                                <button
                                    onClick={handleImportSubmit}
                                    disabled={validCount === 0 || isSubmitting}
                                    className="btn btn-primary import-action-btn"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Loader2 size={16} className="animate-spin" />
                                            Importing...
                                        </>
                                    ) : (
                                        <>
                                            <Play size={16} />
                                            Import {validCount} Valid Goal{validCount !== 1 ? 's' : ''}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                    {/* Row Detail Inspection Modal */}
                    {selectedRowDetail && (
                        <div className="row-detail-modal-overlay" onClick={() => setSelectedRowDetail(null)}>
                            <div className="row-detail-modal-content" onClick={(e) => e.stopPropagation()}>
                                <div className="modal-header">
                                    <div className="modal-header-title">
                                        <FileText size={20} className="modal-icon" />
                                        <h3>Mapped Goal Details — Row #{selectedRowDetail.goal.rowNumber}</h3>
                                    </div>
                                    <button className="close-modal-btn" onClick={() => setSelectedRowDetail(null)}>
                                        <X size={18} />
                                    </button>
                                </div>
                                <div className="detail-modal-body">
                                    <div className="detail-status-banner">
                                        <span className="detail-label">Validation Status:</span>
                                        {selectedRowDetail.goal.isValid ? (
                                            <span className="status-indicator status-valid">
                                                <Check size={14} /> Ready for import
                                            </span>
                                        ) : (
                                            <span className="status-indicator status-invalid">
                                                <AlertCircle size={14} /> {selectedRowDetail.goal.errors.join(', ')}
                                            </span>
                                        )}
                                    </div>

                                    <div className="detail-grid">
                                        <div className="detail-grid-card">
                                            <span className="detail-label">Title</span>
                                            <div className="detail-val-text">{selectedRowDetail.goal.title || <em className="empty-val">Not mapped (None)</em>}</div>
                                        </div>
                                        <div className="detail-grid-card">
                                            <span className="detail-label">Priority</span>
                                            <div>
                                                <span className={`priority-pill priority-${selectedRowDetail.goal.priority}`}>
                                                    {selectedRowDetail.goal.priority}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="detail-grid-card">
                                            <span className="detail-label">Category</span>
                                            <div className="detail-val-text">
                                                {selectedRowDetail.goal.categories && selectedRowDetail.goal.categories.length > 0 ? (
                                                    selectedRowDetail.goal.categories.map((c, ci) => <span key={ci} className="category-pill">{c}</span>)
                                                ) : (
                                                    <em className="empty-val">None</em>
                                                )}
                                            </div>
                                        </div>
                                        <div className="detail-grid-card">
                                            <span className="detail-label">Tags</span>
                                            <div className="detail-val-text">
                                                {selectedRowDetail.goal.tags && selectedRowDetail.goal.tags.length > 0 ? (
                                                    selectedRowDetail.goal.tags.map((t, ti) => <span key={ti} className="tag-pill">{t}</span>)
                                                ) : (
                                                    <em className="empty-val">None</em>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="detail-section">
                                        <span className="detail-section-title">Mapped Goal Description (Sent to AI Agent):</span>
                                        <pre className="detail-goal-text-box">
                                            {selectedRowDetail.goal.goalText || <em className="empty-val">(Empty Goal Description)</em>}
                                        </pre>
                                    </div>

                                    <div className="detail-section">
                                        <span className="detail-section-title">Raw Source Data from File:</span>
                                        <div className="raw-data-table-wrapper">
                                            <table className="raw-data-table">
                                                <thead>
                                                    <tr>
                                                        <th>Column Header</th>
                                                        <th>Source File Value</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {headers.map((h, i) => (
                                                        <tr key={i}>
                                                            <td className="raw-col-header">{h}</td>
                                                            <td className="raw-col-val">{String(selectedRowDetail.goal.rawData?.[i] ?? '') || <em className="empty-val">(empty)</em>}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ImportGoals;
